// SUB-OFFICE 7 — the pushdown FSM. Every state the game will ever have is
// declared here; every transition is also a chyron line (the state machine
// and the narration are the same table). This slice fully implements the
// world-roaming path (BOOT -> ROAM and back); everything else is a declared
// state with an entry line and a TODO hook for the gameplay slice.
//
// GATES: movement and interact keys are dead outside their states, enforced
// here and honored by input.ts. DOM focus is a second, independent gate.
//
// STUB CONTRACT (gameplay slice implements; names are final):
//   fsm.to('SUMMONS')        after bus 'summons' fires (window lights, M8)
//   fsm.to('COLLECT')        E at the summoning window slot
//   fsm.to('COMPOSE')        seated at a desk; camera 900ms two-phase rise
//   fsm.to('SUBMIT_CHAIN')   lever -> canister -> plunger hold-to-slam
//   fsm.to('ADJUDICATION_FULL' | 'ADJUDICATION_EXPEDITED') on plunger slam
//   fsm.to('VERDICT') -> fsm.to('ACK') -> fsm.to('ROAM')
//   fsm.push/pop overlays: CASEFILE, GALLERY, SLIP, SETTINGS
//   specials: RECESS, APPEALS, PERSONNEL_REVIEW, FORM99V, PURGE, REISSUE,
//             INDUSTRIAL_ACTION, INTAKE_FIRST, RETURN_GATE, ABSENCE_ACCOUNTING

import type { GameState, OverlayState } from '../shared/types';
import type { EventBus } from './entities/entities';

/** Entry narration for every declared state (the transition IS the line). */
const ENTRY_LINES: Record<GameState, string | null> = {
  BOOT: null, // the dark before the tube catches has no words
  // The lot has no words for you. The chyron is SILENT until the threshold
  // (graphics Inc 7 flow); the exterior cornice is driven by hand near the
  // door, not by this table. So arrival narrates nothing on entry.
  ARRIVAL_EXTERIOR: null,
  INTAKE_FIRST: 'INTAKE COMMENCES. STATE NOTHING UNTIL A FORM ASKS.',
  RETURN_GATE: 'THE TURNSTILE RECOGNIZES YOU. RECOGNITION IS NOT A GREETING.',
  ABSENCE_ACCOUNTING: 'YOUR ABSENCE REQUIRES ACCOUNTING. A FORM HAS BEEN PREPARED. IT IS ABOUT YOU.',
  ROAM: 'SUB-OFFICE 7 IS OPEN. IT WAS NOT CLOSED. PROCEED.',
  // The vertical core narrates itself through its own controller-driven chyron
  // cornice (the Machine's voice travels with you between slabs), not this table.
  LIFT_LOBBY: null,
  ELEVATOR: null,
  DESCENT_STUB: null,
  SUMMONS: 'A WINDOW IS LIT. THE WINDOW IS LIT FOR YOU. DO NOT KEEP IT LIT LONG.',
  COLLECT: 'TAKE THE FORM. THE FORM IS ALREADY YOURS. IT WAS ALWAYS GOING TO BE.',
  COMPOSE: 'THE PAPER IS BEFORE YOU. THE BUREAU IS BEHIND YOU. BEGIN.',
  SUBMIT_CHAIN: 'ROLL. CARRY. SLAM. THE ORDER IS THE ORDER.',
  ADJUDICATION_FULL: 'YOUR OFFERING IS BEING CONSIDERED. DO NOT BE ELSEWHERE.',
  ADJUDICATION_EXPEDITED: 'EXPEDITED PROCESSING. THE BUREAU KNOWS YOU BY NOW.',
  VERDICT: 'A VERDICT EXISTS. IT IS BEING PRINTED AT A DIGNIFIED RATE.',
  ACK: 'ACKNOWLEDGE THE FINDING. ACKNOWLEDGMENT IS NOT AGREEMENT. IT IS FILING.',
  PHYSICAL_TASK: 'A DUTY OF THE BODY. THE BUREAU MEASURES HANDS AS IT MEASURES SENTENCES.',
  RECESS: 'RECESS IS ASSIGNED. THIRTY SECONDS. THE BUREAU WILL COUNT THEM.',
  APPEALS: 'THE OFFICE OF APPEALS IS OPEN. THE CLERK REMEMBERS MOST OF THEM.',
  PERSONNEL_REVIEW: 'PERSONNEL REVIEW. FIVE FILINGS, CONSIDERED TOGETHER. STAND WHERE YOU ARE.',
  FORM99V: 'FORM 99-V. STATE WHY THE BUREAU SHOULD CEASE PROCESSING YOU.',
  PURGE: 'PROCESSING CONCLUDES. THE WINDOWS WILL NOW CLOSE IN ORDER.',
  REISSUE: 'IDENTITY REISSUANCE IN PROGRESS. THE COPIER IS WARM.',
  INDUSTRIAL_ACTION: 'INDUSTRIAL ACTION: THE MACHINE IS TAKING ITS MANDATED RECESS. YOUR OFFERING HAS BEEN QUEUED.',
};

// Movement lives in the walking states — including the adjudication wait
// (restlessness must be POSSIBLE for the Bureau to file it) and the intake /
// return flows. COMPOSE and the slip states keep the keys dead.
const MOVEMENT_STATES: ReadonlySet<GameState> = new Set([
  'ARRIVAL_EXTERIOR', // the walk across the lot is yours to make
  // the lift lobby and the descent stub are yours to walk; ELEVATOR is the
  // masked ride (frozen — the cab decides where you go, not your legs).
  'LIFT_LOBBY', 'DESCENT_STUB',
  'ROAM', 'SUMMONS', 'COLLECT', 'SUBMIT_CHAIN', 'PHYSICAL_TASK',
  'ADJUDICATION_FULL', 'ADJUDICATION_EXPEDITED',
  'INTAKE_FIRST', 'RETURN_GATE', 'ABSENCE_ACCOUNTING', 'PURGE', 'REISSUE',
] as GameState[]);

const INTERACT_STATES: ReadonlySet<GameState> = new Set([
  'ARRIVAL_EXTERIOR', // E at the door is the one verb outside
  'ROAM', 'SUMMONS', 'COLLECT', 'SUBMIT_CHAIN', 'PHYSICAL_TASK',
  'INTAKE_FIRST', 'RETURN_GATE', 'ABSENCE_ACCOUNTING', 'PURGE', 'REISSUE',
] as GameState[]);

export class Fsm {
  private stack: GameState[] = ['BOOT'];
  private overlays: OverlayState[] = [];

  constructor(private bus: EventBus) {
    bus.on('escape', () => this.popOverlay());
  }

  get state(): GameState {
    return this.stack[this.stack.length - 1];
  }

  get overlay(): OverlayState | null {
    return this.overlays.length ? this.overlays[this.overlays.length - 1] : null;
  }

  /** Replace the top state. Every transition narrates itself. */
  to(next: GameState): void {
    const from = this.state;
    if (from === next) return;
    this.stack[this.stack.length - 1] = next;
    this.announce(from, next);
  }

  /** Pushdown: enter a state to return from (RECESS over ROAM, etc.). */
  push(next: GameState): void {
    const from = this.state;
    this.stack.push(next);
    this.announce(from, next);
  }

  pop(): void {
    if (this.stack.length <= 1) return;
    const from = this.state;
    this.stack.pop();
    this.announce(from, this.state);
  }

  pushOverlay(o: OverlayState): void {
    this.overlays.push(o);
  }

  popOverlay(): void {
    this.overlays.pop();
  }

  private announce(from: GameState, to: GameState): void {
    this.bus.emit('fsm', { from, to });
    const line = ENTRY_LINES[to];
    if (line) this.bus.emit('chyron', line);
  }

  /** WASD/arrows live only here (and die when a textarea owns focus). */
  movementAllowed(): boolean {
    return this.overlay === null && MOVEMENT_STATES.has(this.state);
  }

  /** The single interact verb lives only here. */
  interactAllowed(): boolean {
    return this.overlay === null && INTERACT_STATES.has(this.state);
  }
}
